import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, Box, Snackbar, Alert } from "@mui/material";
import { Layout } from "../../components/layout";
import PageHeader from "../../components/PageHeader";
import PersonsInfo from "../../components/PersonsInfo";
import PageSection from "../../components/PageSection";
import StatusBadge from "../../components/StatusBadge";
import {
  treatmentPlanService,
  TreatmentPlanResponse,
} from "../../services/treatmentPlanService";
import { selectUser } from "../../store/selectors/authSelectors";
import { PROVIDER_ROLES } from "../../constants";
import { EditNoteIcon } from "../../components/Icons";
import {
  useModal,
  ApproveConfirmationModalContent,
  QRCodeSignModalContent,
} from "../../components/Modal";
import {
  TreatmentAppointment,
  AppointmentTimeSeparator,
  TreatmentPlanFooter,
} from "./components";
import { downloadTreatmentPlanPdf } from "../../utils/downloadTreatmentPlanPdf";
import {
  RejectionCommentBox,
  RejectionCommentTitle,
  RejectionCommentText,
} from "./TreatmentPlanPage.styled";

const TreatmentPlanPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const user = useSelector(selectUser);
  const [treatmentPlan, setTreatmentPlan] =
    useState<TreatmentPlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const fetchInitiatedRef = useRef(false);

  // Check if user is Provider (can edit treatment plan)
  const isProvider = PROVIDER_ROLES.includes(
    user?.roleTitle as (typeof PROVIDER_ROLES)[number],
  );

  const fetchTreatmentPlan = useCallback(
    async (force = false) => {
      if (!id) return;
      if (!force && fetchInitiatedRef.current) return;

      fetchInitiatedRef.current = true;

      try {
        setLoading(true);
        setError(null);
        const data = await treatmentPlanService.getById(id);
        setTreatmentPlan(data);
      } catch (err) {
        setError(t("treatmentPlan.failedToLoad"));
        console.error("Error fetching treatment plan:", err);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  useEffect(() => {
    fetchTreatmentPlan();
  }, [fetchTreatmentPlan]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Treatment Plan Statuses:
  // Treatment plan statuses:
  // -1: GeneratingTriggered - provider initiated treatment plan generation
  // 0: Generating - AI is currently generating the treatment plan
  // 1: AIDrafted - AI has generated the treatment plan, awaiting provider review
  // 2: AwaitingProviderReview - waiting for provider's review and approval
  // 3: AwaitingPatientConfirmation - provider approved, waiting for patient confirmation
  // 4: DeclinedByPatient - patient declined the treatment plan
  // 5: ApprovedByPatient - patient approved the treatment plan
  // 6: InProgress - treatment plan is in progress (active treatment)
  // 7: Completed - treatment plan completed
  // 8: GenerationFailed - generation failed

  const getStatusLabel = (status: number) => {
    switch (status) {
      case -1:
        return t("treatmentPlan.status.generatingTriggered");
      case 0:
        return t("treatmentPlan.status.generating");
      case 1:
        return t("treatmentPlan.status.aiDrafted");
      case 2:
        return t("treatmentPlan.status.awaitingProviderReview");
      case 3:
        return t("treatmentPlan.status.awaitingPatientConfirmation");
      case 4:
        return t("treatmentPlan.status.declinedByPatient");
      case 5:
        return t("treatmentPlan.status.approvedByPatient");
      case 6:
        return t("treatmentPlan.status.inProgress");
      case 7:
        return t("treatmentPlan.status.completed");
      case 8:
        return t("treatmentPlan.status.generationFailed");
      default:
        return t("treatmentPlan.status.unknown");
    }
  };

  const getStatusType = (status: number) => {
    switch (status) {
      case -1:
      case 0:
        return "pending";
      case 1:
      case 2:
        return "pending";
      case 3:
        return "in-progress";
      case 4:
        return "declined-by-patient";
      case 5:
      case 6:
        return "approved";
      case 7:
        return "completed";
      case 8:
        return "error";
      default:
        return "pending";
    }
  };

  const handleEditPlan = () => {
    navigate(`/treatment-plan/${id}/edit`);
  };

  const handleDownloadPdf = async () => {
    if (!treatmentPlan) return;
    try {
      await downloadTreatmentPlanPdf({ treatmentPlan });
    } catch (err) {
      console.error("Error downloading PDF:", err);
    }
  };

  const handleEmailForReview = async () => {
    if (!id) return;

    try {
      await treatmentPlanService.shareByEmail(id);
      setEmailSuccess(true);
    } catch (err) {
      console.error("Error sending email for review:", err);
      setEmailError(true);
    }
  };

  const handleOpenToSign = () => {
    if (!id) return;
    openModal({
      id: "qr-code-sign",
      content: <QRCodeSignModalContent treatmentPlanId={id} />,
      showCloseButton: true,
    });
  };

  const handleApprove = () => {
    if (!user?.id || !id) return;

    openModal({
      id: "approve-confirmation-modal",
      title: "",
      content: (
        <ApproveConfirmationModalContent
          isLoading={isApproving}
          onCancel={() => {
            closeModal("approve-confirmation-modal");
          }}
          onApprove={async () => {
            try {
              setIsApproving(true);
              await treatmentPlanService.approve(id, user.id);
              closeModal("approve-confirmation-modal");
              fetchTreatmentPlan(true);
            } catch (err) {
              console.error("Error approving treatment plan:", err);
            } finally {
              setIsApproving(false);
            }
          }}
        />
      ),
      showCloseButton: true,
      maxWidth: "600px",
      containerPadding: "60px 90px",
      contentPadding: "0",
    });
  };

  if (loading) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Layout>
    );
  }

  if (error || !treatmentPlan) {
    return (
      <Layout>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            gap: 2,
          }}
        >
          <div>{error || t("treatmentPlan.notFound")}</div>
          <Button onClick={() => fetchTreatmentPlan(true)}>
            {t("treatmentPlan.retry")}
          </Button>
        </Box>
      </Layout>
    );
  }

  // Get sorted appointments
  const sortedAppointments = [...treatmentPlan.appointments].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  // Check if treatment plan has options (any appointment with conditionalPath)
  // If any appointment has conditionalPath, total fee should be 0
  const hasOptions = treatmentPlan.appointments.some(
    (appointment) =>
      appointment.conditionalPath && appointment.conditionalPath.trim() !== "",
  );

  // Prepare persons data
  const getPatientName = () => {
    if (treatmentPlan.patientFirstName || treatmentPlan.patientLastName) {
      return `${treatmentPlan.patientFirstName || ""} ${
        treatmentPlan.patientLastName || ""
      }`.trim();
    }
    return `Patient #${treatmentPlan.patientUid}`;
  };

  const getProviderName = () => {
    if (treatmentPlan.providerFirstName || treatmentPlan.providerLastName) {
      return `${treatmentPlan.providerFirstName || ""} ${
        treatmentPlan.providerLastName || ""
      }`.trim();
    }
    return `Provider #${treatmentPlan.providerId}`;
  };

  // Prepare meta items for header
  const metaItems = [
    { text: formatDate(treatmentPlan.dateGenerated), key: "date" },
    { text: formatTime(treatmentPlan.dateGenerated), key: "time" },
  ];

  // Show "Approved by Patient" for status 5, otherwise show "Approved on" for provider approval
  if (treatmentPlan.status === 5 && treatmentPlan.approvedByPatientAt) {
    metaItems.push({
      text: t("treatmentPlan.approvedByPatient", {
        name: getPatientName(),
        date: formatDate(treatmentPlan.approvedByPatientAt),
        time: formatTime(treatmentPlan.approvedByPatientAt),
      }),
      key: "approvedByPatient",
    });
  } else if (treatmentPlan.approvedByProviderAt) {
    metaItems.push({
      text: t("treatmentPlan.approvedOn", {
        date: formatDate(treatmentPlan.approvedByProviderAt),
      }),
      key: "approved",
    });
  }

  const persons = [
    {
      name: getPatientName(),
      role: t("treatmentPlan.patient"),
    },
    {
      name: getProviderName(),
      role: t("treatmentPlan.initiatedBy"),
      avatarUrl: treatmentPlan.providerAvatarUrl || undefined,
    },
  ];

  return (
    <Layout>
      <PageHeader
        title={treatmentPlan.name}
        badge={
          <StatusBadge
            status={getStatusType(treatmentPlan.status)}
            label={getStatusLabel(treatmentPlan.status)}
          />
        }
        metaItems={metaItems}
        actions={
          // Hide Edit button for Completed status (7)
          isProvider && treatmentPlan.status !== 7 ? (
            <Button variant="outlined" onClick={handleEditPlan}>
              <EditNoteIcon width="20px" height="20px" />
              {t("treatmentPlan.editTreatmentPlan")}
            </Button>
          ) : undefined
        }
      />

      {treatmentPlan.status === 4 && treatmentPlan.rejectionComment && (
        <RejectionCommentBox>
          <RejectionCommentTitle>
            {t("treatmentPlan.commentFromPatient")}
          </RejectionCommentTitle>
          <RejectionCommentText>
            {treatmentPlan.rejectionComment}
          </RejectionCommentText>
        </RejectionCommentBox>
      )}

      <PersonsInfo persons={persons} />

      <PageSection
        title={t("treatmentPlan.treatmentSummary")}
        content={treatmentPlan.treatmentSummary}
        allowHtml
      />

      {treatmentPlan.providerNote && (
        <PageSection
          title={t("treatmentPlan.noteFromProvider")}
          content={treatmentPlan.providerNote}
        />
      )}

      <PageSection title={t("treatmentPlan.treatmentSteps")}>
        {sortedAppointments.map((appointment, index) => (
          <React.Fragment key={appointment.id}>
            <TreatmentAppointment
              appointment={appointment}
              appointmentNumber={index + 1}
            />
            {index < sortedAppointments.length - 1 &&
              appointment.timeframeLabel && (
                <AppointmentTimeSeparator
                  timeframeLabel={appointment.timeframeLabel}
                />
              )}
          </React.Fragment>
        ))}
      </PageSection>

      <TreatmentPlanFooter
        totalFee={treatmentPlan.totalFee}
        status={treatmentPlan.status}
        isProvider={isProvider}
        hasMultipleOptions={hasOptions}
        onApprove={handleApprove}
        onDownloadPdf={handleDownloadPdf}
        onEmailForReview={handleEmailForReview}
        onOpenToSign={handleOpenToSign}
      />

      <Snackbar
        open={emailError}
        autoHideDuration={6000}
        onClose={() => setEmailError(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setEmailError(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {t("treatmentPlan.emailSendFailed")}
        </Alert>
      </Snackbar>

      <Snackbar
        open={emailSuccess}
        autoHideDuration={6000}
        onClose={() => setEmailSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setEmailSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {t("treatmentPlan.emailSentSuccess")}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default TreatmentPlanPage;
